<template>
  <div>
    <div
      class="content-list-table search-async-api-data vs-con-loading__container"
      :class="{ 'content-list-table--loading': loading }"
    >
      <vs-table-m
        :sst="true"
        :total="selectedItems.totalItems"
        :maxItems="pageSize"
        :data="selectedItems.items"
        :emitWhenOpenRow="true"
        :showExpandableData="relatedContentByType.hasOwnProperty(typeSelected)"
        class="mt-10"
        @change-page="$emit('change-page', $event)"
        @sort="$emit('sort', $event)"
        @selected="handleSelected"
        pagination
      >
        <template slot="header">
          <div class="flex items-center w-full">
            <h4 class="m-2 my-4">Content list</h4>
            <div class="ml-auto mr-6 text-lg font-semibold">
              {{ selectedItems.totalItems }} results
            </div>
          </div>
        </template>
        <template slot="thead">
          <vs-th
            sort-key="id"
            v-if="columnsByType[typeSelected].includes('id')"
          >
            ID
          </vs-th>
          <vs-th
            sort-key="name"
            v-if="columnsByType[typeSelected].includes('name')"
          >
            Name
          </vs-th>
          <vs-th
            sort-key="version"
            v-if="columnsByType[typeSelected].includes('version')"
          >
            Version
          </vs-th>
          <vs-th
            sort-key="type"
            v-if="columnsByType[typeSelected].includes('type')"
          >
            Type
          </vs-th>
          <vs-th
            sort-key="avg_rating"
            v-if="columnsByType[typeSelected].includes('avg_rating')"
          >
            Avg. Rating
          </vs-th>
          <vs-th
            sort-key="created"
            v-if="columnsByType[typeSelected].includes('created')"
          >
            Created
          </vs-th>
          <vs-th
            sort-key="jwplayer_id"
            v-if="columnsByType[typeSelected].includes('jwplayer_id')"
          >
            jwPlayer ID
          </vs-th>
          <vs-th
            sort-key="published_date"
            v-if="columnsByType[typeSelected].includes('published_date')"
          >
            Published on
          </vs-th>
          <vs-th
            sort-key="modified"
            v-if="columnsByType[typeSelected].includes('modified')"
          >
            Modified
          </vs-th>
          <vs-th
            sort-key="modified_by"
            v-if="columnsByType[typeSelected].includes('modified_by')"
          >
            Modified By
          </vs-th>
          <vs-th
            sort-key="comments"
            v-if="columnsByType[typeSelected].includes('comments')"
          >
            Comments
          </vs-th>
          <vs-th
            sort-key="start_datetime"
            v-if="columnsByType[typeSelected].includes('start_datetime')"
          >
            Starts on
          </vs-th>
          <vs-th
            sort-key="end_datetime"
            v-if="columnsByType[typeSelected].includes('end_datetime')"
          >
            Ends on
          </vs-th>
          <vs-th
            sort-key="zoom_webinar_id"
            v-if="columnsByType[typeSelected].includes('zoom_webinar_id')"
          >
            Zoom ID
          </vs-th>
          <vs-th
            sort-key="number_of_questions"
            v-if="columnsByType[typeSelected].includes('number_of_questions')"
          >
            Number of questions
          </vs-th>
          <vs-th
            sort-key="video_reference"
            v-if="columnsByType[typeSelected].includes('video_reference')"
          >
            Video Reference
          </vs-th>
          <vs-th
            sort-key="lab_type"
            v-if="columnsByType[typeSelected].includes('lab_type')"
          >
            Lab type
          </vs-th>
          <vs-th
            sort-key="pta_sdn"
            v-if="
              columnsByType[typeSelected].includes('pta_sdn') &&
              filterLabs.lab_type === 'pta'
            "
          >
            PTA SDN
          </vs-th>
          <vs-th
            sort-key="els_lab_id"
            v-if="
              columnsByType[typeSelected].includes('els_lab_id') &&
              filterLabs.lab_type === 'els'
            "
          >
            ELS Lab ID
          </vs-th>
          <vs-th
            sort-key="status"
            v-if="columnsByType[typeSelected].includes('status')"
          >
            Status
          </vs-th>
          <vs-th
            sort-key="id"
            v-if="columnsByType[typeSelected].includes('skill_area_id')"
          >
            Skill Area ID
          </vs-th>
          <vs-th sort-key="no-sort"> Actions </vs-th>
        </template>
        <template slot-scope="{ data }">
          <template v-if="loading">
            <vs-tr
              v-for="index in skeletonRowsCount"
              :key="`skeleton-${index}`"
              class="content-list-table__row-skeleton"
            >
              <vs-td
                v-for="column in visibleColumnsForType"
                :key="`skeleton-${index}-${column}`"
              >
                <span
                  class="content-list-table__skeleton"
                  :class="skeletonClassByColumn(column)"
                ></span>
              </vs-td>
            </vs-tr>
          </template>
          <template v-else>
            <template v-for="tr in data">
              <vs-tr
                :data="tr"
                :key="tr.id"
                :class="
                  tr.isDraft
                    ? 'content-list-table__row-subItem'
                    : 'content-list-table__row-mainItem'
                "
              >
                <vs-td
                  :data="tr.id"
                  :style="getCellStyle('id', tr.id)"
                  v-if="columnsByType[typeSelected].includes('id')"
                >
                  {{ tr.id | shortenUUID }}
                </vs-td>

                <vs-td
                  :data="tr.name"
                  :style="getCellStyle('name', tr.name)"
                  v-if="columnsByType[typeSelected].includes('name')"
                >
                  <slot
                    name="name-cell"
                    :item="tr"
                    :typeSelected="typeSelected"
                  >
                    {{ tr.name }}
                  </slot>
                </vs-td>

                <vs-td
                  :data="tr.version"
                  :style="getCellStyle('version', tr.version)"
                  v-if="columnsByType[typeSelected].includes('version')"
                >
                  {{ tr.version }}
                </vs-td>

                <vs-td
                  :data="tr.type"
                  class="type-cell"
                  :class="`type-${tr.type}`"
                  :style="getCellStyle('type', tr.type)"
                  v-if="columnsByType[typeSelected].includes('type')"
                >
                  {{ tr.type }}
                </vs-td>

                <vs-td
                  :data="tr.avg_rating"
                  :style="getCellStyle('avg_rating', tr.avg_rating)"
                  v-if="columnsByType[typeSelected].includes('avg_rating')"
                >
                  <rating-stars
                    :ref="`course_${tr.id}`"
                    v-if="tr.avg_rating"
                    @mouseenter.native="
                      showStarWidget(`course_${tr.id}`, tr.id, 'course')
                    "
                    @mouseleave.native="widgetActive = false"
                    :rating="
                      tr.avg_rating ? parseFloat(tr.avg_rating) : tr.avg_rating
                    "
                  />
                </vs-td>

                <vs-td
                  :data="tr.created"
                  :style="getCellStyle('created', tr.created)"
                  v-if="columnsByType[typeSelected].includes('created')"
                >
                  {{ tr.created | dateFormat }}
                </vs-td>

                <vs-td
                  :data="tr.jwplayer_id"
                  :style="getCellStyle('jwplayer_id', tr.jwplayer_id)"
                  v-if="columnsByType[typeSelected].includes('jwplayer_id')"
                >
                  <a
                    @click.stop="$event.stopImmediatePropagation()"
                    :href="`https://dashboard.jwplayer.com/#/content/detail?key=${tr.jwplayer_id}`"
                    target="_blank"
                    >{{ tr.jwplayer_id }}</a
                  >
                </vs-td>

                <vs-td
                  :data="tr.published_date"
                  :style="getCellStyle('published_date', tr.published_date)"
                  v-if="columnsByType[typeSelected].includes('published_date')"
                >
                  {{ tr.published_date | dateFormat }}
                </vs-td>

                <vs-td
                  :data="tr.modified"
                  :style="getCellStyle('modified', tr.modified)"
                  v-if="columnsByType[typeSelected].includes('modified')"
                >
                  {{ tr.modified | dateFormat }}
                </vs-td>

                <vs-td
                  :data="tr.modified_by"
                  :style="getCellStyle('modified_by', tr.modified_by)"
                  v-if="columnsByType[typeSelected].includes('modified_by')"
                >
                  {{ tr.modified_by }}
                </vs-td>

                <vs-td
                  :data="tr.comments"
                  :style="getCellStyle('comments', tr.comments)"
                  v-if="columnsByType[typeSelected].includes('comments')"
                >
                  {{ tr.comments }}
                </vs-td>

                <vs-td
                  :data="tr.number_of_questions"
                  :style="
                    getCellStyle('number_of_questions', tr.number_of_questions)
                  "
                  v-if="
                    columnsByType[typeSelected].includes('number_of_questions')
                  "
                >
                  {{ tr.number_of_questions || 0 }}
                </vs-td>

                <vs-td
                  :data="tr.metadata.video_reference"
                  :style="
                    getCellStyle('video_reference', tr.metadata.video_reference)
                  "
                  v-if="
                    tr.metadata &&
                    columnsByType[typeSelected].includes('video_reference')
                  "
                >
                  {{ getVideoReferenceName(tr.metadata.video_reference) }}
                </vs-td>

                <vs-td
                  :data="tr.start_datetime"
                  :style="getCellStyle('start_datetime', tr.start_datetime)"
                  v-if="columnsByType[typeSelected].includes('start_datetime')"
                >
                  {{ tr.start_datetime | dateFormat }}
                </vs-td>

                <vs-td
                  :data="tr.end_datetime"
                  :style="getCellStyle('end_datetime', tr.end_datetime)"
                  v-if="columnsByType[typeSelected].includes('end_datetime')"
                >
                  {{ tr.end_datetime | dateFormat }}
                </vs-td>

                <vs-td
                  :data="tr.zoom_webinar_id"
                  :style="getCellStyle('zoom_webinar_id', tr.zoom_webinar_id)"
                  v-if="columnsByType[typeSelected].includes('zoom_webinar_id')"
                >
                  {{ tr.zoom_webinar_id }}
                </vs-td>

                <vs-td
                  :data="tr.lab_type"
                  :style="getCellStyle('lab_type', tr.lab_type)"
                  v-if="columnsByType[typeSelected].includes('lab_type')"
                >
                  {{ tr.lab_type }}
                </vs-td>

                <vs-td
                  :data="tr.metadata.pta_sdn"
                  :style="getCellStyle('pta_sdn', tr.metadata.pta_sdn)"
                  v-if="
                    columnsByType[typeSelected].includes('pta_sdn') &&
                    filterLabs.lab_type === 'pta'
                  "
                >
                  {{ tr.metadata.pta_sdn }}
                </vs-td>
                <vs-td
                  :data="tr.skill_area_id"
                  :style="getCellStyle('skill_area_id', tr.skill_area_id)"
                  v-if="columnsByType[typeSelected].includes('skill_area_id')"
                >
                  {{ tr.skill_area_id }}
                </vs-td>

                <vs-td
                  :data="tr.metadata.els_lab_id"
                  :style="getCellStyle('els_lab_id', tr.metadata.els_lab_id)"
                  v-if="
                    columnsByType[typeSelected].includes('els_lab_id') &&
                    filterLabs.lab_type === 'els'
                  "
                >
                  {{ tr.metadata.els_lab_id }}
                </vs-td>

                <vs-td
                  class="status-cell"
                  :class="`status-${tr.status}`"
                  :style="getCellStyle('status', tr.status)"
                  :data="tr.status"
                  v-if="columnsByType[typeSelected].includes('status')"
                >
                  {{ readableStatus(tr.status) }}
                </vs-td>

                <vs-td :data="tr.id">
                  <router-link
                    v-if="!withSelection && canEditItem(tr)"
                    :to="getEditRoute(tr)"
                    @click.native.stop="$event.stopImmediatePropagation()"
                  >
                    <feather-icon
                      icon="EditIcon"
                      svgClasses="h-5 w-5"
                      class="ml-1"
                    />
                  </router-link>
                  <span
                    v-else-if="!withSelection"
                    class="is-disabled-link"
                    @click.stop
                  >
                    <feather-icon
                      icon="EditIcon"
                      svgClasses="h-5 w-5"
                      class="ml-1"
                    />
                  </span>
                  <vs-button
                    type="border"
                    v-else
                    @click.stop="
                      $emit('select-item', { type: typeSelected, item: tr })
                    "
                  >
                    <feather-icon
                      v-if="isSelected(tr.id)"
                      icon="CheckIcon"
                      svgClasses="h-5 w-5 color-current text-blue"
                      class="flex items-center mx-3"
                    />
                    <span v-else>Select</span>
                  </vs-button>
                </vs-td>
              </vs-tr>
              <vs-tr :key="`${tr.id}-related`">
                <td
                  :colspan="visibleColumnsForType.length"
                  v-if="
                    relatedContentByType.hasOwnProperty(typeSelected) &&
                    rowsOpened.includes(tr.id) &&
                    !rowsLoading.includes(tr.id)
                  "
                >
                  <h4 class="mt-5">Related content</h4>
                  <RelatedContentList
                    wide
                    :metadata="buildItemMetadata(tr)"
                    :allowedParents="relatedContentByType[typeSelected]"
                  />
                </td>
              </vs-tr>
            </template>
          </template>
        </template>
      </vs-table-m>
    </div>
    <rating-widget
      @mouseenter.native="widgetHover = true"
      @mouseleave.native="widgetHover = false"
      v-if="widgetActive || widgetHover"
      :style="widgetPosition"
      :content_id="widgetContentId"
      :content_type="widgetContentType"
    />
  </div>
</template>

<script>
import VsTableM from '@/components/VsTableM'
import RatingStars from '@/components/RatingStars.vue'
import RatingWidget from '@/components/RatingWidget.vue'
import RelatedContentList from './RelatedContentList.vue'
import { starsWidget } from '@/utils/mixins'

export default {
  name: 'ContentListTable',
  mixins: [starsWidget],
  components: {
    'vs-table-m': VsTableM,
    RatingStars,
    RatingWidget,
    RelatedContentList,
  },
  data() {
    return {
      cellStyleCache: Object.create(null),
    }
  },
  props: {
    items: { type: Object, required: true },
    typeSelected: { type: String, required: true },
    pageSize: { type: Number, required: true },
    columnsByType: { type: Object, required: true },
    relatedContentByType: {
      type: Object,
      default: () => ({}),
    },
    rowsOpened: {
      type: Array,
      default: () => [],
    },
    rowsLoading: {
      type: Array,
      default: () => [],
    },
    withSelection: {
      type: Boolean,
      default: false,
    },
    navigateOnSelect: {
      type: Boolean,
      default: false,
    },
    filterLabs: {
      type: Object,
      default: () => ({ lab_type: null }),
    },
    itemsSelected: {
      type: Object,
      default: () => ({}),
    },
    editPathResolver: {
      type: Function,
      default: null,
    },
    editRouteParamsResolver: {
      type: Function,
      default: null,
    },
    editRouteResolver: {
      type: Function,
      default: null,
    },
    cellStylesByType: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    skeletonRowsCount: {
      type: Number,
      default: 6,
    },
  },
  computed: {
    selectedItems() {
      return this.items[this.typeSelected] || { items: [], totalItems: 0 }
    },
    visibleColumnsForType() {
      const columns = this.columnsByType[this.typeSelected] || []
      const filteredColumns = columns.filter((column) => {
        if (column === 'pta_sdn') return this.filterLabs.lab_type === 'pta'
        if (column === 'els_lab_id') return this.filterLabs.lab_type === 'els'
        return true
      })
      return [...filteredColumns, 'actions']
    },
  },
  watch: {
    typeSelected() {
      this.resetCellStyleCache()
    },
    cellStylesByType() {
      this.resetCellStyleCache()
    },
  },
  methods: {
    handleSelected(item) {
      if (this.navigateOnSelect && !this.withSelection) {
        const route = this.getEditRoute(item)
        if (route && route.name) {
          this.$router.push(route)
        }
      }

      this.$emit('selected', item)
    },
    isSelected(id) {
      return Boolean(
        this.itemsSelected &&
          this.itemsSelected[this.typeSelected] &&
          this.itemsSelected[this.typeSelected].includes(id)
      )
    },
    buildItemMetadata(item) {
      if (this.typeSelected === 'Collection') {
        return {
          learning_paths: (item.learning_paths || []).map((lp) => lp.id),
        }
      }
      return item.metadata
    },
    readableStatus(status) {
      if (!status) return ''
      return (
        status.charAt(0).toUpperCase() +
        status.slice(1).toLowerCase().replace(/_/g, ' ')
      )
    },
    getVideoReferenceName(ref) {
      if (!ref) return ''
      const reference = ref.split('/')
      return reference[reference.length - 1]
    },
    getEditRouteParams(item) {
      if (typeof this.editRouteParamsResolver === 'function') {
        return (
          this.editRouteParamsResolver(item, this.typeSelected) || {
            id: item.id,
          }
        )
      }

      return { id: item.id }
    },
    getEditRoute(item) {
      if (typeof this.editRouteResolver === 'function') {
        return this.editRouteResolver(item, this.typeSelected) || {}
      }

      const name =
        typeof this.editPathResolver === 'function'
          ? this.editPathResolver(this.typeSelected) || ''
          : ''
      const params =
        typeof this.editRouteParamsResolver === 'function'
          ? this.editRouteParamsResolver(item, this.typeSelected) || {
              id: item.id,
            }
          : { id: item.id }

      return name ? { name, params } : {}
    },
    canEditItem(item) {
      return Boolean(this.getEditRoute(item).name)
    },
    resetCellStyleCache() {
      this.cellStyleCache = Object.create(null)
    },
    getColumnStyleConfig(column) {
      const stylesForType = this.cellStylesByType[this.typeSelected] || {}
      return stylesForType[column] || {}
    },
    getCellStyle(column, value) {
      const cacheKey = `${this.typeSelected}:${column}:${
        value == null ? '' : String(value)
      }`
      if (this.cellStyleCache[cacheKey]) return this.cellStyleCache[cacheKey]

      const columnStyleConfig = this.getColumnStyleConfig(column)
      const style = columnStyleConfig[value] || columnStyleConfig.default || {}

      if (column === 'status') {
        this.cellStyleCache[cacheKey] = {
          '--status-bg': style.background || 'transparent',
          '--status-color': style.color || 'inherit',
          '--status-radius': style.borderRadius || '0',
          '--status-padding': style.padding || '0',
        }

        return this.cellStyleCache[cacheKey]
      }

      if (column === 'type') {
        this.cellStyleCache[cacheKey] = {
          '--type-bg': style.background || 'transparent',
          '--type-color': style.color || 'inherit',
          '--type-radius': style.borderRadius || '0',
          '--type-padding': style.padding || '0',
        }

        return this.cellStyleCache[cacheKey]
      }

      this.cellStyleCache[cacheKey] = style
      return this.cellStyleCache[cacheKey]
    },
    skeletonClassByColumn(column) {
      if (column === 'actions') return 'content-list-table__skeleton--icon'
      if (column === 'id') return 'content-list-table__skeleton--id'

      const smallColumns = [
        'version',
        'type',
        'avg_rating',
        'created',
        'published_date',
        'modified',
        'start_datetime',
        'end_datetime',
        'number_of_questions',
        'lab_type',
        'skill_area_id',
        'status',
        'comments',
      ]

      return smallColumns.includes(column)
        ? 'content-list-table__skeleton--small'
        : ''
    },
  },
}
</script>

<style>
.content-list-table .status-cell > span {
  background: var(--status-bg);
  color: var(--status-color);
  border-radius: var(--status-radius);
  padding: var(--status-padding);
  display: inline-block;
}

.content-list-table .type-cell > span {
  background: var(--type-bg);
  color: var(--type-color);
  border-radius: var(--type-radius);
  padding: var(--type-padding);
  display: inline-block;
}

.content-list-table .is-disabled-link {
  opacity: 0.45;
  pointer-events: none;
  cursor: not-allowed;
}

.content-list-table
  .vs-con-table
  .tr-values.content-list-table__row-subItem
  .vs-table--td {
  padding-top: 0;
  padding-bottom: 0;
}

.content-list-table .vs-con-table .tr-values.content-list-table__row-subItem {
  color: rgb(107, 114, 128);
  background: rgb(255, 253, 245);
}

.content-list-table.content-list-table--loading .vs-table--not-data {
  display: none;
}

.content-list-table .content-list-table__row-skeleton .vs-table--td {
  pointer-events: none;
}

.content-list-table .content-list-table__skeleton {
  display: inline-block;
  width: 100%;
  max-width: 180px;
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef2f7 25%, #e4e9f2 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: content-list-table-skeleton-loading 1.2s ease-in-out infinite;
}

.content-list-table .content-list-table__skeleton--small {
  max-width: 90px;
}

.content-list-table .content-list-table__skeleton--id {
  max-width: 70px;
}

.content-list-table .content-list-table__skeleton--icon {
  max-width: 18px;
  height: 18px;
  border-radius: 50%;
}

@keyframes content-list-table-skeleton-loading {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}
</style>
